import 'react-international-phone/style.css';

import { BrandType } from 'noddi-types';
import { cn } from 'noddi-ui-common';
import { ApiErrorMessage, ErrorCodes, NoddiPhoneInput, useAppForm } from 'noddi-ui-web';
import { commonTexts, isPhoneNumberValid, LanguageCodeType } from 'noddi-util';
import { ReactNode, useId } from 'react';
import type { SMSLoginStepRenderer } from './smsLoginStep';
import { useSendVerificationSms } from './useSendVerificationSms';
import { phoneNumberDto } from './util';

type Props = {
  mobileNumber: string;
  setMobileNumber: (mobileNumber: string) => void;
  /** Called after the send succeeds, with the number it went to; the input may have changed while sending. */
  onCodeSent: (mobileNumber: string) => void;
  brandName?: BrandType;
  hideText?: boolean;
  /** Keeps focus where it is — for a form that is not the page's main task. */
  disableAutoFocus?: boolean;
  /** Field label above the input. Omit for the bare input the login screens use. */
  phoneNumberLabel?: string;
  /** Submit sits beside the field (full width under it on phones) — for a compact card that is not the page's main task. */
  inlineSubmit?: boolean;
  /** Rendered under the submit, on this step only, so code-step copy never inherits it. */
  footer?: ReactNode;
  /** Places the send-code button outside the form, e.g. in a fixed footer. */
  renderStep?: SMSLoginStepRenderer;
  translations: {
    phoneNumber?: string;
    enterNumberToReceiveVerificationCode: string;
    sendCode: string;
  };
  language?: LanguageCodeType;
  errorCodes: ErrorCodes;
};

export const PhoneNumberForm = ({
  mobileNumber,
  setMobileNumber,
  onCodeSent,
  brandName,
  hideText,
  disableAutoFocus,
  phoneNumberLabel,
  inlineSubmit,
  footer,
  renderStep,
  translations,
  language,
  errorCodes
}: Props) => {
  const formId = useId();
  const { send, errorFor } = useSendVerificationSms({ brandName, isResend: false });
  const phoneNumberIsValid = isPhoneNumberValid(mobileNumber);
  const sendError = errorFor(phoneNumberDto({ mobileNumber }));

  // The number lives in SMSLogin for the code step; the form only tracks the in-flight send.
  const form = useAppForm({
    defaultValues: {},
    onSubmit: async () => {
      const submittedNumber = mobileNumber;
      if (isPhoneNumberValid(submittedNumber) && (await send(phoneNumberDto({ mobileNumber: submittedNumber })))) {
        onCodeSent(submittedNumber);
      }
    }
  });

  const content = (
    <form
      id={formId}
      className='flex flex-col gap-3'
      onSubmit={(event) => {
        event.preventDefault();
        event.stopPropagation();
        void form.handleSubmit();
      }}
    >
      {!hideText && <p className='text-sm'>{translations.enterNumberToReceiveVerificationCode}</p>}
      <div className={cn('flex flex-col gap-3', inlineSubmit && 'sm:flex-row sm:items-end')}>
        <div className='min-w-0 flex-1'>
          <NoddiPhoneInput
            language={language}
            label={phoneNumberLabel}
            aria-label={phoneNumberLabel ?? translations.phoneNumber ?? commonTexts.phoneNumber}
            onChange={setMobileNumber}
            value={mobileNumber}
            disableAutoFocus={disableAutoFocus}
            // A page whose only task is this number gets the large field.
            size={renderStep ? 'large' : 'default'}
          />
        </div>
        {!renderStep && (
          <form.SubscribeButton
            disabled={!phoneNumberIsValid}
            className={inlineSubmit ? 'w-full sm:w-auto' : 'ml-auto'}
            label={translations.sendCode}
            keepsFocus
          />
        )}
      </div>
      {sendError && <ApiErrorMessage errorCodes={errorCodes} error={sendError} />}
      {footer}
    </form>
  );

  return (
    <form.AppForm>
      {renderStep
        ? renderStep({
            content,
            // Outside the <form> but tied to it by id, so Enter in the field and the button submit alike.
            submit: (
              <form.SubscribeButton
                formId={formId}
                disabled={!phoneNumberIsValid}
                label={translations.sendCode}
                endIcon='ArrowRight'
                keepsFocus
              />
            )
          })
        : content}
    </form.AppForm>
  );
};
